import { createIndexedDB } from '@/shared/utils';

const CACHE_META_KEY = 'vcc_vton_result_v1';
const IMAGE_STORE_KEY = 'result-image';
const UPLOAD_COOLDOWN_KEY = 'vcc_vton_upload_cooldown_v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

const vtonImageDB = createIndexedDB({
  dbName: 'vcc-vton',
  storeName: 'v1',
  version: 1,
});

interface VtonCacheMeta {
  cachedAt: number;
  isFallback: boolean;
}

export interface VtonCacheEntry {
  imageObjectUrl: string;
  isFallback: boolean;
}

function readMeta(): VtonCacheMeta | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(CACHE_META_KEY);
    return raw ? (JSON.parse(raw) as VtonCacheMeta) : null;
  } catch {
    return null;
  }
}

function writeMeta(meta: VtonCacheMeta): void {
  try {
    localStorage.setItem(CACHE_META_KEY, JSON.stringify(meta));
  } catch {
    /* empty — storage full or unavailable */
  }
}

function removeMeta(): void {
  try {
    localStorage.removeItem(CACHE_META_KEY);
  } catch {
    /* empty */
  }
}

/**
 * 캐시된 VTON 결과를 로드한다.
 * TTL(7일)이 만료되었거나 blob을 읽을 수 없으면 null을 반환한다.
 * 반환된 imageObjectUrl은 호출자가 URL.revokeObjectURL로 해제해야 한다.
 */
export async function loadVtonCache(): Promise<VtonCacheEntry | null> {
  const meta = readMeta();
  if (!meta) return null;

  if (Date.now() - meta.cachedAt >= TTL_MS) {
    await clearVtonCache();
    return null;
  }

  const blob = await vtonImageDB.load<Blob>(IMAGE_STORE_KEY);
  if (!blob) {
    removeMeta();
    return null;
  }

  return {
    imageObjectUrl: URL.createObjectURL(blob),
    isFallback: meta.isFallback,
  };
}

export async function saveVtonCache(
  imageUrl: string,
  isFallback: boolean,
): Promise<void> {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return;

    const blob = await response.blob();
    await vtonImageDB.save(IMAGE_STORE_KEY, blob);
    writeMeta({ cachedAt: Date.now(), isFallback });
  } catch {
    /* empty */
  }
}

async function clearVtonCache(): Promise<void> {
  removeMeta();

  try {
    await vtonImageDB.remove(IMAGE_STORE_KEY);
  } catch {
    /* empty */
  }
}

export function isVtonUploadOnCooldown(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const raw = localStorage.getItem(UPLOAD_COOLDOWN_KEY);
    if (!raw) return false;

    const { usedAt } = JSON.parse(raw) as { usedAt: number };
    return Date.now() - usedAt < TTL_MS;
  } catch {
    return false;
  }
}

export function saveVtonUploadCooldown(): void {
  try {
    localStorage.setItem(
      UPLOAD_COOLDOWN_KEY,
      JSON.stringify({ usedAt: Date.now() }),
    );
  } catch {
    /* storage full or unavailable */
  }
}
